import { useEffect, useState } from 'react';
import {
  Combobox, useListCollection, useFilter,
  ProgressCircle,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import type { UserType } from '@customTypes/user';

type Props = {
  errors: FormErrors | null;
  users: UserType[];
  onSearch: (value: string) => Promise<void>;
  onChange: (user: UserType | null) => void;
  label?: string;
  showSelectedValue?: boolean;
  value?: UserType | null;
}

const UserSelect = ({
  errors, users, onSearch, onChange, label = 'Usuário', showSelectedValue = false, value = null,
}: Props) => {
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();
  const [searching, setSearching] = useState(false);
  const [inputValue, setInputValue] = useState(showSelectedValue && value ? value.name : '');

  const { contains } = useFilter({ sensitivity: 'base' });

  const { collection, filter, reset, set } = useListCollection({
    initialItems: users,
    itemToValue: (item) => item.id,
    itemToString: (item) => item.name,
    filter: contains,
  });

  const handleInputValueChange = (value: string) => {
    setInputValue(value);
    filter(value);
    setSearching(true);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(async () => {
      await onSearch(value);
      setSearching(false);
    }, 1000));
  };

  const handleValueChange = (user: UserType | null) => {
    onChange(user);
    setInputValue(showSelectedValue ? (user?.name ?? '') : '');
  };

  useEffect(() => {
    if (users) {
      set(users);
    }
  }, [users, set]);

  return (
    <Combobox.Root
      collection={collection}
      value={showSelectedValue && value ? [value.id] : []}
      inputValue={inputValue}
      onInputValueChange={(e) => handleInputValueChange(e.inputValue)}
      onValueChange={({ items }) => handleValueChange(items[0] ?? null)}
      invalid={!!errors?.user}
      variant="subtle"
      openOnClick
    >
      <Combobox.Label>{label}</Combobox.Label>
      <Combobox.Control>
        <Combobox.Input placeholder="Pesquisar..." />
        <Combobox.IndicatorGroup>
          {searching && (
            <ProgressCircle.Root value={null} size="xs">
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
          )}
          <Combobox.ClearTrigger />
          <Combobox.Trigger onClick={reset} />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content zIndex="popover">
          <Combobox.Empty>Nenhum item encontrado</Combobox.Empty>
          {collection.items.map((item) => (
            <Combobox.Item item={item} key={item.id}>
              {item.name}
              <Combobox.ItemIndicator />
            </Combobox.Item>
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  );
};

export default UserSelect;
